import React, { useContext, useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { QUICK_CONNECT_EXCHANGES } from "@shared/common/constants";
import { hexToRGB } from "@shared/utils/misc";
import { NoAccountCardHighlightContext } from "features/bots/BotForm/common/NoAccountCardHighlight.context";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup";
import useNoExchangeConnectedActions from "features/configuration/common/useNoExchangeConnectedActions";
import Button from "common/Buttons/Button";
import Text from "common/Typography/Text";
import { phoneMaxWidth } from "common/NewLook/layout";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
} from "common/NewLook/tokens";
import {
  BOT_FORM_NO_ACCOUNT_CARD_ID,
  BOT_FORM_NO_ACCOUNT_CONNECT_ID,
  BOT_FORM_NO_ACCOUNT_SWITCH_WORKSPACE_ID,
} from "testIds.ts";

const NoAccountConnectCard = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { subGroup } = useContext(BotFormContext) || {};
  const { highlightCount } = useContext(NoAccountCardHighlightContext);
  const cardRef = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(false);
  const {
    displayData,
    trackViewed,
    onCreateClick,
    onSwitchWorkspaceCreateClick,
    whopModal,
  } = useNoExchangeConnectedActions({
    placement: "bot_form",
    redirectOnClick: false,
  });
  const { isDemo } = displayData;

  useEffect(() => {
    trackViewed();
  }, [trackViewed]);

  useEffect(() => {
    const card = cardRef.current;
    if (!highlightCount || !card) return;
    if (!getIsFullyVisible(card))
      card.scrollIntoView({ block: "center", behavior: "smooth" });
    setHighlighted(false);
    const frame = requestAnimationFrame(() => setHighlighted(true));
    const duration = window.matchMedia(REDUCED_MOTION_QUERY).matches
      ? REDUCED_MOTION_HIGHLIGHT_MS
      : HIGHLIGHT_MS;
    const timeout = setTimeout(() => setHighlighted(false), duration);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
    };
  }, [highlightCount]);

  const getDescription = () => {
    if (subGroup?.name)
      return isDemo
        ? t("no_account_card.description_demo_group", {
            groupName: subGroup.name,
          })
        : t("no_account_card.description_group", {
            groupName: subGroup.name,
          });
    return isDemo
      ? t("no_account_card.description_demo")
      : t("no_account_card.description");
  };

  return (
    <Card
      ref={cardRef}
      data-testid={BOT_FORM_NO_ACCOUNT_CARD_ID}
      data-highlighted={highlighted ? "true" : "false"}
    >
      <ExchangesIconsGroup
        exchanges={CARD_EXCHANGES}
        size={28}
        maxCount={5}
        style={{ marginBottom: 12 }}
      />
      <CardTitle>
        {isDemo ? t("no_account_card.title_demo") : t("no_account_card.title")}
      </CardTitle>
      <CardDescription type="secondary">{getDescription()}</CardDescription>
      <ConnectButton
        data-testid={BOT_FORM_NO_ACCOUNT_CONNECT_ID}
        block={true}
        onClick={() => onCreateClick()}
      >
        {isDemo ? displayData.buttonText : t("no_account_card.button")}
      </ConnectButton>
      <SwitchWorkspaceButton
        data-testid={BOT_FORM_NO_ACCOUNT_SWITCH_WORKSPACE_ID}
        type="link"
        block={true}
        onClick={onSwitchWorkspaceCreateClick}
      >
        {isDemo
          ? tCommon("no_exchange_connected.secondary_button_demo")
          : tCommon("no_exchange_connected.secondary_button")}
      </SwitchWorkspaceButton>
      {whopModal}
    </Card>
  );
};

export default NoAccountConnectCard;

const CARD_EXCHANGES = [...QUICK_CONNECT_EXCHANGES];
const HIGHLIGHT_MS = 2200;
const REDUCED_MOTION_HIGHLIGHT_MS = 1500;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const getIsFullyVisible = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  return rect.top >= 0 && rect.bottom <= window.innerHeight;
};

const getRing = (color: string, glow: number, glowAlpha = 0.25) =>
  `0 0 0 2px ${color}, 0 0 0 ${2 + glow}px ${hexToRGB(color, glowAlpha)}`;

const cardPulse = (color: string) => keyframes`
  0% { box-shadow: ${getRing(color, 0)}; }
  16% { box-shadow: ${getRing(color, 6)}; }
  32% { box-shadow: ${getRing(color, 1)}; }
  48% { box-shadow: ${getRing(color, 6)}; }
  64% { box-shadow: ${getRing(color, 1)}; }
  82% { box-shadow: ${getRing(color, 1)}; }
  100% { box-shadow: 0 0 0 2px transparent, 0 0 0 2px transparent; }
`;

const buttonEmphasis = (color: string) => keyframes`
  0%, 82% { border-color: ${color}; color: ${color}; }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px 16px 8px;
  border: ${(p) => p.theme.borders.medium};
  border-radius: 8px;
  background: ${(p) => p.theme.colors.cardBgColor};
  &[data-highlighted="true"] {
    animation: ${(p) => cardPulse(p.theme.colors.primaryBlue)} ${HIGHLIGHT_MS}ms
      ease-out forwards;
    @media ${REDUCED_MOTION_QUERY} {
      animation: none;
      box-shadow: 0 0 0 2px ${(p) => p.theme.colors.primaryBlue};
    }
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border: 1px solid ${getNewLookTokens(p.theme).hairline};
      border-radius: ${NEW_LOOK_RADIUS.panel}px;
      background: ${getNewLookTokens(p.theme).well};
    `};
`;
const CardTitle = styled(Text).attrs({ as: "div" })`
  color: ${(p) => p.theme.colors.titleColor};
  ${newLookPhoneType.cardTitle};
`;
const CardDescription = styled(Text)`
  margin-top: 4px;
  font-size: 13px;
  line-height: 18px;
`;
const ConnectButton = styled(Button)`
  margin-top: 16px;
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  ${Card}[data-highlighted="true"] && {
    animation: ${(p) => buttonEmphasis(p.theme.colors.primaryBlue)}
      ${HIGHLIGHT_MS}ms ease-out;
    @media ${REDUCED_MOTION_QUERY} {
      animation: none;
      border-color: ${(p) => p.theme.colors.primaryBlue};
      color: ${(p) => p.theme.colors.primaryBlue};
    }
  }
`;
const SwitchWorkspaceButton = styled(Button)`
  margin-top: 4px;
  height: 36px;
  &.ant-btn-link {
    color: ${(p) => p.theme.colors.secondaryText} !important;
    font-size: 13px;
    font-weight: 400;
    ${(p) =>
      p.theme.isNewLook &&
      css`
        @media (max-width: ${phoneMaxWidth(p)}) {
          ${newLookPhoneType.label};
        }
      `};
  }
  &.ant-btn-link:hover,
  &.ant-btn-link:focus-visible {
    color: ${(p) => p.theme.colors.primaryBlue} !important;
    text-decoration: underline;
  }
`;
